import { formatDateVi } from "./dateUtils";
import { DocxDescriptor } from "./docxBuilder";

// Descriptor conventions
// - Preview descriptors: used by Modals to build data from form state for in-browser preview
// - HistoryDownload descriptors: used by History/List rows to build data from persisted items for download

// Import Material (Preview - Modal)
export const materialImportPreviewDescriptor: DocxDescriptor<any, any> = {
    codeField: "import_code",
    codeValue: () => "PHIẾU XEM TRƯỚC",
    date: (s) => s.formData.import_date,
    header: {
        provider_name: (s) => s.formData.provider_name || "",
        deliverer: (s) => s.formData.deliverer || "",
        input_method: (s) => s.formData.input_method || "",
        note: (s) => s.formData.note || "",
    },
    materials: {
        list: (s) => s.selectedMaterials || [],
        item: {
            name: (m) => m.name || "",
            quantity: (m) => m.importQuantity || "",
            measurement_unit: (m) => m.measurement_unit || "",
            missing_quantity: (m) => m.missingQuantity || "",
            code: (m) => m.material_code || m.code || m.id || "",
            storage: (m) => m.storage || "",
        },
    },
};

// Import Material (HistoryDownload - List Row)
export const materialImportHistoryDownloadDescriptor: DocxDescriptor<any, any> = {
    codeField: "import_code",
    codeValue: (row) => row.import_code || "",
    date: (row) => row.import_date,
    header: {
        provider_name: (row) => row.provider_name || "",
        deliverer: (row) => row.deliverer || "",
        input_method: (row) => row.input_method || "",
        note: (row) => row.note || "",
    },
    materials: {
        list: (row) => row.materials || [],
        item: {
            name: (m) => m.material?.name || "",
            quantity: (m) => m.quantity || "",
            measurement_unit: (m) => m.material?.measurement_unit || "",
            code: (m) => m.material?.material_code || m.material?.id || "",
            storage: (m) => m.material?.storage || "",
        },
    },
};

// Export Material (HistoryDownload - List Row)
export const materialExportHistoryDownloadDescriptor: DocxDescriptor<any, any> = {
    codeField: "export_code",
    codeValue: (row) => row.export_code || "",
    date: (row) => row.export_date,
    header: {
        storage: (row) => row.storage || "",
        receiverer_name: (row) => row.receiverer_name || "",
        export_method: (row) => row.export_method || "",
        department: (row) => row.department || "",
        source: (row) => row.source || "",
        note: (row) => row.note || "",
    },
    materials: {
        list: (row) => row.materials || [],
        item: {
            name: (m) => m.material?.name || "",
            quantity: (m) => m.quantity || "",
            measurement_unit: (m) => m.material?.measurement_unit || "",
            code: (m) => m.material?.material_code || m.material?.id || "",
        },
    },
};

// Product (Preview - Modal, Import)
export const productImportPreviewDescriptor: DocxDescriptor<any, any> = {
    codeField: "import_code",
    codeValue: () => "PHIẾU XEM TRƯỚC",
    date: (s) => s.formData.import_date,
    header: {
        customer: (s) => s.formData.customer?.name || s.formData.customer || "",
        receiver_name: (s) => s.formData.receiver_name || "",
        import_method: (s) => s.formData.import_method.charAt(0).toUpperCase() + s.formData.import_method.slice(1) || "",
        note: (s) => s.formData.note || "",
    },
    items: {
        key: "products",
        list: (s) => s.tableItems || [],
        item: {
            name: (p) => p.name || "",
            required_quantity: (p) => p.requiredQuantity || 0,
            quantity: (p) => p.importQuantity || 0,
            measurement_unit: (p) => p.measurement_unit || p.unit || "",
            code: (p) => p.code || p.product_code || p.material_code || p.id || "",
            storage: (p) => p.storage || "",
        },
    },
};

// Product (HistoryDownload - Import list row)
export const productImportHistoryDownloadDescriptor: DocxDescriptor<any, any> = {
    codeField: "import_code",
    codeValue: (row) => row.import_code || "",
    date: (row) => row.import_date,
    header: {
        customer: (row) => row.customer?.name || "",
        receiver_name: (row) => row.receiver_name || "",
        import_method: (row) => row.import_method || "",
        note: (row) => row.note || "",
    },
    items: {
        key: "products",
        list: (row) => row.products || [],
        item: {
            name: (p) => p.product?.name || "",
            quantity: (p) => p.quantity || "",
            code: (p) => p.product?.product_code || p.product?.id || "",
            storage: (p) => p.product?.storage || "",
        },
    },
};

// Product (Preview - Modal, Export)
export const productExportPreviewDescriptor: DocxDescriptor<any, any> = {
    codeField: "export_code",
    codeValue: () => "PHIẾU XEM TRƯỚC",
    date: (s) => s.formData.export_date,
    header: {
        storage: (s) => s.formData.storage || "",
        customer: (s) => {
            return (s.formData as any).customer && typeof (s.formData as any).customer === "object"
                ? (s.formData as any).customer.name
                : (s.formData as any).customer;
        },
        receiver_name: (s) => s.formData.receiver_name || "",
        address: (s) => s.formData.address || "",
        reason: (s) => s.formData.reason || "",
        note: (s) => s.formData.note || "",
    },
    items: {
        key: "products",
        list: (s) => s.selectedProducts || [],
        item: {
            name: (p) => p.name || "",
            required_quantity: (p) => p.requiredQuantity || 0,
            quantity: (p) => p.exportQuantity || 0,
            measurement_unit: (p) => p.measurement_unit || p.unit || "",
            code: (p) => p.code || p.product_code || p.material_code || p.id || "",
        },
    },
};

// Product (HistoryDownload - Export list row)
export const productExportHistoryDownloadDescriptor: DocxDescriptor<any, any> = {
    codeField: "xk_id", // Template expects xk_id as seen in existing usage
    codeValue: (row) => row.export_code || "",
    date: (row) => row.export_date,
    header: {
        storage: (row) => row.storage || "",
        customer: (row) => row.customer || "",
        receiver_name: (row) => row.receiver_name || "",
        address: (row) => row.address || "",
        reason: (row) => row.reason || "",
        note: (row) => row.note || "",
    },
    items: {
        key: "products",
        list: (row) => row.products || [],
        item: {
            name: (p) => p.product?.name || "",
            quantity: (p) => p.quantity || "",
            code: (p) => p.product?.product_code || p.product?.id || "",
        },
    },
};

// Material Request (Preview - Modal, Export)
export const materialRequestPreviewDescriptor: DocxDescriptor<any, any> = {
    codeField: "export_code",
    codeValue: () => "PHIẾU XEM TRƯỚC",
    date: (s) => s.created_at || "",
    header: {
        request_name: (s) => s.request_name || "",
        department: (s) => s.department || "",
        created_at: (s) => formatDateVi(s.created_at) || "",
        sum_quantity: (s) => s.sum_quantity || "",
        sale_order: (s) => s.sale_order || "",
    },
    materials: {
        list: (s) => s.materials || [],
        item: {
            name: (m) => m.name || "",
            code: (m) => m.material_code || m.code || m.id || "",
            measurement_unit: (m) => m.measurement_unit || "",
            quantity: (m) => m.request_quantity || "",
            storage: (m) => m.storage || "",
            conversion_quantity: (m) => {
                if (!m.secondary_measurement_unit) return "";
                const roundTo5Decimal = (value: number): number => {
                    if (!Number.isFinite(value)) return 0;
                    const factor = 10 ** 5;
                    return Math.round((value + Number.EPSILON) * factor) / factor;
                };
                return roundTo5Decimal(m.request_quantity / m.conversion_rate);
            },
            secondary_measurement_unit: (m) => m.secondary_measurement_unit || "",
        },
    },
};
